import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DataTable, {
  PAGE_SIZES,
} from "../../../components/carbon/DataTable/DataTable";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import {
  UserMaterialOpportunitiesTable,
  CompletionPercentage,
  CompanySetupBanner,
  AIProcessingBanner,
} from "../../../components";
import { FileType } from "../../../types";
import { useSupplierRfqs } from "../../../hooks/useOrders";
import {
  buildOrderRows,
  navigateToOrder,
  orderExpandedContent,
  supplierRfqHeaders,
} from "../../Orders/orderListRows";
import useDebounce from "../../../hooks/useDebounce";
import {
  useInvitationsByEmail,
  useUserMaterialOpportunitiesInvitations,
} from "../../../hooks/useMaterialOpportunities";
import { useUser } from "../../../context/hooks/useUser";
import { useCompanyProfile } from "../../../hooks/useCompany";
import { useUserMaterialCategories } from "../../../hooks/useUserMaterialCategories";
import SellerAnalytics from "../../SellerAnalytics/SellerAnalytics";
import "./seller.scss";

/**
 * The seller landing page: setup/processing banners, profile completion, `SellerAnalytics`, then the
 * unified RFQ table and the invitation tables. Migrated to Carbon (supplier-home flow): the two
 * legacy Tailwind RFQ tables (simple `OpportunitiesTable` + joint `JointOrdersTable`) are replaced by
 * ONE server-paginated Carbon `DataTable` fed by `useSupplierRfqs` (`GET /order/suppliers/rfqs`),
 * reusing the shared `orderListRows` machinery — a joint RFQ is a single row whose sub-orders live in
 * its expandable material detail, and a row click opens the simple/joint bid view. The banners and
 * `CompletionPercentage` are consumed as-is (out of scope for this pass).
 */
export default function Seller() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [pageMaterialInvitationsNumber, setPageMaterialInvitationsNumber] =
    useState(0);
  const [pageInvitationsNumber, setPageInvitationsNumber] = useState(0);

  // Unified RFQ table state (server-side pagination + search).
  const [rfqPage, setRfqPage] = useState(0);
  const [rfqPageSize, setRfqPageSize] = useState(PAGE_SIZES[0]);
  const [rfqSearch, setRfqSearch] = useState("");
  const { debouncedSearchTerm } = useDebounce(rfqSearch, 500);

  useEffect(() => {
    setRfqPage(0);
  }, [debouncedSearchTerm]);

  const { user } = useUser();

  const { data: companyProfile } = useCompanyProfile(user?.company?.id || "");
  const { data: userMaterialCategories } = useUserMaterialCategories();

  const rfqParams = useMemo(
    () => ({
      page: rfqPage,
      size: rfqPageSize,
      ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
      sortBy: "finishDate",
      sortDirection: "ASC" as const,
    }),
    [rfqPage, rfqPageSize, debouncedSearchTerm]
  );

  const { data: rfqData, isLoading: rfqLoading } = useSupplierRfqs(rfqParams);
  const rfqRows = useMemo(() => buildOrderRows(rfqData, undefined, true), [rfqData]);
  const rfqHeaders = useMemo(() => supplierRfqHeaders(t), [t]);

  const { data: materialInvitationsOpportunities } =
    useUserMaterialOpportunitiesInvitations(pageMaterialInvitationsNumber);

  const { data: invitationsByEmail } = useInvitationsByEmail(
    pageInvitationsNumber
  );

  // Determine banner state
  const hasWebsite = !!companyProfile?.website;
  const hasProductCatalog = companyProfile?.files?.some(
    (file: any) => file.type === FileType.PRODUCT_CATALOG
  );
  const hasUserMaterialCategories =
    userMaterialCategories && userMaterialCategories.length > 0;

  // Show setup banner only if user has NO material categories AND missing BOTH website AND catalog
  const shouldShowSetupBanner =
    !hasUserMaterialCategories && !hasWebsite && !hasProductCatalog;

  // Show processing banner if user has NO material categories BUT has EITHER website OR catalog (or both)
  const shouldShowProcessingBanner =
    !hasUserMaterialCategories && (hasWebsite || hasProductCatalog);

  return (
    <Fragment>
      {shouldShowSetupBanner && (
        <CompanySetupBanner companyProfile={companyProfile} />
      )}

      {shouldShowProcessingBanner && <AIProcessingBanner />}

      <CompletionPercentage />

      <SellerAnalytics />

      {materialInvitationsOpportunities?.content?.length > 0 && (
        <UserMaterialOpportunitiesTable
          pageNumber={pageMaterialInvitationsNumber}
          setPageNumber={setPageMaterialInvitationsNumber}
          materialInvitationsOpportunities={materialInvitationsOpportunities}
        />
      )}

      {invitationsByEmail?.content?.length > 0 && (
        <UserMaterialOpportunitiesTable
          pageNumber={pageInvitationsNumber}
          setPageNumber={setPageInvitationsNumber}
          materialInvitationsOpportunities={invitationsByEmail}
        />
      )}

      <section className="seller__rfqs">
        <h2 className="seller__section-title">{t("material.tenders")}</h2>
        <DataTable
          headers={rfqHeaders}
          rows={rfqRows}
          isLoading={rfqLoading}
          search={{
            value: rfqSearch,
            onChange: setRfqSearch,
            label: t("cta.search"),
          }}
          pagination={{
            page: rfqPage + 1,
            pageSize: rfqPageSize,
            pageSizes: PAGE_SIZES,
            totalItems: rfqData?.totalElements ?? 0,
            onPageChange: (newPage, newPageSize) => {
              setRfqPage(newPage - 1);
              setRfqPageSize(newPageSize);
            },
          }}
          onRowClick={(row) => navigateToOrder(navigate, row)}
          getExpandedContent={(row) => orderExpandedContent(row, t)}
          empty={<EmptyState title={t("opportunities.noOpportunities")} />}
        />
      </section>
    </Fragment>
  );
}
